import { SiteLayout } from "@/components/SiteLayout";
import { useState } from "react";
import { ArrowRight } from "lucide-react";
import { Img, SIZES } from "@/components/Img";
import { type Locale } from "@/i18n/config";
import { pageHead } from "@/i18n/head";
import { kontaktStrings } from "@/i18n/content/kontakt";

/** <head> for the contact page. */
export function kontaktHead(locale: Locale) {
  const s = kontaktStrings[locale];
  return pageHead("kontakt", locale, s.meta, { breadcrumbName: s.breadcrumbName });
}

export function KontaktPage({ locale }: { locale: Locale }) {
  const t = kontaktStrings[locale];
  const [status, setStatus] = useState<"idle" | "sending" | "sent" | "error">("idle");

  async function handleSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (status === "sending") return;
    const form = e.currentTarget;
    const data = Object.fromEntries(new FormData(form).entries());
    setStatus("sending");
    try {
      const res = await fetch("/api/contact", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
      });
      const json = await res.json().catch(() => ({ ok: false }));
      if (res.ok && json.ok) {
        setStatus("sent");
        form.reset();
      } else {
        setStatus("error");
      }
    } catch {
      setStatus("error");
    }
  }

  return (
    <SiteLayout>
      {/* Hero */}
      <section className="py-20 md:py-28">
        <div className="mx-auto max-w-4xl px-4 md:px-8 text-center">
          <p className="text-sm uppercase tracking-widest text-foreground/70">{t.hero.eyebrow}</p>
          <h1 className="mt-4 text-4xl md:text-6xl font-bold tracking-tight">{t.hero.title}</h1>
          <p className="mt-6 text-lg text-foreground/85 leading-relaxed">{t.hero.body}</p>
        </div>
      </section>

      {/* Info + form */}
      <section className="bg-section-light text-section-light-foreground py-20">
        <div className="mx-auto max-w-7xl px-4 md:px-8 grid gap-12 lg:grid-cols-2">
          {/* Info */}
          <div>
            <p className="text-sm uppercase tracking-widest opacity-70">{t.info.eyebrow}</p>
            <h2 className="mt-4 text-3xl md:text-4xl font-bold tracking-tight">{t.info.heading}</h2>
            <div className="mt-8 space-y-6 text-lg">
              <div>
                <a href="tel:+4555551212" className="hover:text-primary">+45 5555 1212</a>
              </div>
              <div>
                <a href="mailto:info@misupport.dk" className="hover:text-primary">info@misupport.dk</a>
              </div>
              <div>{t.info.address}</div>
              <div>{t.info.cvrLabel}: 2579 3714</div>
            </div>
            <div className="mt-10 pt-8 border-t border-section-light-foreground/20 space-y-2 text-base">
              <div className="font-semibold">{t.info.bankName}</div>
              <div>{t.info.bankRegLabel}: 6818</div>
              <div>{t.info.bankAccountLabel}: 1099042</div>
            </div>
          </div>

          {/* Form */}
          <form
            onSubmit={handleSubmit}
            className="rounded-2xl bg-background text-foreground p-8 md:p-10 space-y-5"
          >
            <div>
              <label className="block text-sm font-medium mb-2" htmlFor="navn">{t.form.navn}</label>
              <input id="navn" name="navn" required type="text" autoComplete="name" className="w-full rounded-lg bg-card border border-white/10 px-4 py-3 outline-none focus:border-primary" />
            </div>
            <div className="grid gap-5 md:grid-cols-2">
              <div>
                <label className="block text-sm font-medium mb-2" htmlFor="email">{t.form.email}</label>
                <input id="email" name="email" required type="email" autoComplete="email" className="w-full rounded-lg bg-card border border-white/10 px-4 py-3 outline-none focus:border-primary" />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2" htmlFor="mobil">{t.form.mobil}</label>
                <input id="mobil" name="mobil" type="tel" autoComplete="tel" className="w-full rounded-lg bg-card border border-white/10 px-4 py-3 outline-none focus:border-primary" />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium mb-2" htmlFor="firma">{t.form.firma}</label>
              <input id="firma" name="firma" type="text" autoComplete="organization" className="w-full rounded-lg bg-card border border-white/10 px-4 py-3 outline-none focus:border-primary" />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2" htmlFor="besked">{t.form.besked}</label>
              <textarea id="besked" name="besked" required rows={5} className="w-full rounded-lg bg-card border border-white/10 px-4 py-3 outline-none focus:border-primary resize-none" />
            </div>
            {/* Honeypot — hidden from users; bots that fill it are silently dropped. */}
            <input type="text" name="company_url" tabIndex={-1} autoComplete="off" aria-hidden="true" className="hidden" />
            <button type="submit" disabled={status === "sending"} className="btn-cta disabled:opacity-60">
              {status === "sending" ? t.form.sending : t.form.submit} <ArrowRight className="h-4 w-4" />
            </button>
            {status === "sent" && (
              <p className="text-sm text-primary mt-2">{t.form.success}</p>
            )}
            {status === "error" && (
              <p className="text-sm text-destructive mt-2">
                {t.form.errorPrefix}
                <a href="mailto:info@misupport.dk" className="underline">info@misupport.dk</a>
                {t.form.errorSuffix}
              </p>
            )}
          </form>
        </div>
      </section>

      {/* Ledelsen */}
      <section className="bg-section-light text-section-light-foreground py-20">
        <div className="mx-auto max-w-7xl px-4 md:px-8">
          <p className="text-sm uppercase tracking-widest opacity-70 text-center">{t.team.eyebrow}</p>
          <h2 className="mt-4 text-3xl md:text-4xl font-bold tracking-tight text-center">{t.team.heading}</h2>
          <div className="mt-12 grid gap-8 sm:grid-cols-2 lg:grid-cols-4">
            {t.team.members.map((p) => (
              <div key={p.name} className="text-center">
                <Img src={p.img} alt={p.name} className="rounded-2xl w-full h-auto object-cover aspect-[3/4]" sizes={SIZES.portrait} />
                <h3 className="mt-4 font-semibold text-lg">{p.name}</h3>
                <p className="text-sm opacity-70">{p.role}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Er du vores nye kollega */}
      <section className="bg-secondary py-20">
        <div className="mx-auto max-w-7xl px-4 md:px-8 grid gap-12 lg:grid-cols-2 items-center">
          <Img
            src="/wp-content/uploads/MISupportIT_NTK4170-scaled.jpg"
            alt={t.career.heading}
            className="rounded-2xl w-full aspect-[4/3] object-cover order-2 lg:order-1"
            sizes={SIZES.split}
          />
          <div className="order-1 lg:order-2">
            <h2 className="text-3xl md:text-4xl font-bold tracking-tight">{t.career.heading}</h2>
            <p className="mt-6 text-foreground/95 leading-relaxed">{t.career.p1}</p>
            <p className="mt-4 text-foreground/95 leading-relaxed">{t.career.p2}</p>
            <a
              href={t.career.href}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-8 btn-cta"
            >
              {t.career.cta} <ArrowRight className="h-4 w-4" />
            </a>
          </div>
        </div>
      </section>
    </SiteLayout>
  );
}
